import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, RotateCcw, Armchair, Loader2, Coins, Layers } from 'lucide-react';
import { AppMode, ImageAsset, ROOM_LABELS } from '../types';
import { HistoryPanel } from './HistoryPanel';

// Safe localStorage helpers — guards against Safari private mode and storage quota errors.
function safeGetItem(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage unavailable (private mode / quota) — silently ignore.
  }
}

const LABEL_KEY = 'stagery_export_label';
const DEFAULT_LABEL = 'Digitale herinrichting';

interface Props {
  config: { mode: AppMode; roomType: string };
  images: ImageAsset;
  showEmptyBase: boolean;
  setShowEmptyBase: (v: boolean) => void;
  regenerateFeedback: string;
  setRegenerateFeedback: (v: string) => void;
  isLoading: boolean;
  onRegenerate: () => void;
  onRestyle: () => void;
  /** BUG-131 - doorwerken op de render die nu getoond wordt. */
  onIterate: () => void;
  canIterate: boolean;
  propertyId: number | null;
  historyVersion: number;
  onHistoryRestore: (url: string) => void;
}

export const StudioControls: React.FC<Props> = ({
  config, images, showEmptyBase, setShowEmptyBase,
  regenerateFeedback, setRegenerateFeedback, isLoading,
  onRegenerate, onRestyle, onIterate, canIterate, propertyId, historyVersion, onHistoryRestore,
}) => {
  const roomLabel = ROOM_LABELS[config.roomType as keyof typeof ROOM_LABELS] ?? config.roomType;
  const modeLabel = config.mode === AppMode.FULL_RENOVATION ? 'Volledig restylen' : 'Meubels toevoegen';

  // Use state for the export label so the input is a controlled component and
  // localStorage reads happen only on mount (avoids calling localStorage on every render).
  const [exportLabel, setExportLabel] = useState<string>(() =>
    safeGetItem(LABEL_KEY, DEFAULT_LABEL)
  );

  // Persist label changes to localStorage safely.
  useEffect(() => {
    safeSetItem(LABEL_KEY, exportLabel);
  }, [exportLabel]);

  return (
    <>
      <div className="p-4 bg-white border border-black/6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <h3 className="text-base font-bold text-ink tracking-[-0.01em] mb-1.5">Actief project</h3>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-ink/60">
          <span>{roomLabel}</span>
          <span className="text-ink/25">·</span>
          <span>{modeLabel}</span>
        </div>
        <div className="mt-3 pt-3 border-t border-black/6 flex items-center gap-2">
          <span className="text-xs font-semibold text-ink/55">Label</span>
          <input
            value={exportLabel}
            onChange={e => setExportLabel(e.target.value)}
            className="text-xs text-ink bg-transparent border-b border-black/10 focus:border-accent focus:outline-none px-1 py-1 flex-1"
          />
        </div>
      </div>

      <div className="space-y-3">
        <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center gap-2">
          <Eye size={13} />
          Weergave
        </label>
        {config.mode === AppMode.FULL_RENOVATION && images.renovated ? (
          <button
            onClick={() => setShowEmptyBase(!showEmptyBase)}
            className={`w-full px-4 py-3 border flex items-center justify-between transition-all rounded-xl text-sm font-semibold ${showEmptyBase ? 'bg-ink text-white border-ink shadow-[0_6px_18px_-8px_rgba(0,0,0,0.3)]' : 'bg-white text-ink border-black/10 hover:border-black/20 hover:bg-black/[0.02]'}`}
          >
            <span>Toon lege ruimte</span>
            {showEmptyBase ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        ) : (
          <div className="px-4 py-3 border border-dashed border-black/10 text-xs text-ink/45 text-center rounded-xl">
            Niet beschikbaar bij "Meubels toevoegen".
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center gap-2">
          <RotateCcw size={13} />
          Opnieuw proberen
        </label>
        <div>
          <label className="block text-xs font-semibold text-ink/65 mb-1.5">
            Wat ging er fout? <span className="text-ink/40 font-normal">(optioneel)</span>
          </label>
          <textarea
            value={regenerateFeedback}
            onChange={e => setRegenerateFeedback(e.target.value)}
            placeholder="bijv: raam verzonnen in linkermuur, bank staat verkeerd, vloer is te donker…"
            rows={3}
            disabled={isLoading}
            className="w-full bg-white border border-black/10 rounded-xl p-3 text-sm leading-relaxed resize-none focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all placeholder:text-ink/35"
          />
        </div>
        <button
          onClick={onRegenerate}
          disabled={isLoading}
          className="w-full bg-accent text-white px-4 py-3 text-sm font-bold hover:bg-accent/90 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-all flex items-center justify-center gap-2 shadow-[0_8px_22px_-8px_rgba(241,110,0,0.5)] rounded-xl"
        >
          {isLoading ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
          Opnieuw restylen
        </button>
        {/* Credit cost notice — shown before the user clicks so they know the cost upfront. */}
        <div className="flex items-center justify-center gap-1.5 text-xs text-ink/50">
          <Coins size={12} className="shrink-0" />
          <span>1 credit per nieuwe restyling</span>
        </div>
        <button
          onClick={onRestyle}
          disabled={isLoading}
          className="w-full bg-white border border-black/10 text-ink px-4 py-3 text-sm font-semibold hover:border-accent/40 hover:bg-accent/5 disabled:opacity-60 transition-all flex items-center justify-center gap-2 rounded-xl"
        >
          {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Armchair size={15} />}
          Alleen meubels vernieuwen
        </button>
        <p className="text-xs text-ink/55 leading-relaxed text-center">
          Zelfde ruimte, andere meubels.
        </p>

        {/* BUG-131 - doorwerken op het resultaat dat nu op het scherm staat.
            De twee knoppen hierboven beginnen allebei opnieuw bij de originele
            foto. Deze niet: die neemt de huidige render als vertrekpunt, zodat
            je erop kunt stapelen. */}
        {canIterate && (
          <>
            <div className="pt-1">
              <button
                onClick={onIterate}
                disabled={isLoading}
                className="w-full bg-white border border-black/10 text-ink px-4 py-3 text-sm font-semibold hover:border-accent/40 hover:bg-accent/5 disabled:opacity-60 transition-all flex items-center justify-center gap-2 rounded-xl"
              >
                {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Layers size={15} />}
                Doorwerken op dit resultaat
              </button>
            </div>
            <p className="text-xs text-ink/55 leading-relaxed text-center">
              Bouwt verder op wat je nu ziet. Beschrijf hierboven wat er nog bij moet.
            </p>
          </>
        )}
      </div>

      <HistoryPanel
        propertyId={propertyId}
        version={historyVersion}
        onRestore={onHistoryRestore}
      />
    </>
  );
};
